import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as AttaimaLogo } from 'assets/icons/attaima.svg';
import { clsx } from 'clsx';

import type ILaunchpadSplashProps from './interfaces/ILaunchpadSplashProps';

import styles from './sass/LaunchpadSplash.module.scss';

function getCompanyInitials(companyName?: string): string {
  if (!companyName) {
    return '';
  }

  return companyName
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function LaunchpadSplash({
  splashPhase,
  companyName,
  companyLogo,
  userName,
}: ILaunchpadSplashProps) {
  const { t } = useTranslation();
  const [isLogoBroken, setIsLogoBroken] = useState(false);
  const normalizedCompanyLogo = useMemo(() => companyLogo?.trim() || '', [companyLogo]);

  useEffect(() => {
    setIsLogoBroken(false);
  }, [normalizedCompanyLogo]);

  if (splashPhase === 'hidden') {
    return null;
  }

  const isFading = splashPhase === 'fading';
  const showCompanyLogo = Boolean(normalizedCompanyLogo) && !isLogoBroken;

  return (
    <div
      className={clsx(styles.root, { [styles.fadeOut]: isFading })}
      aria-hidden={isFading}
      role="status"
      aria-live="polite"
    >
      <div className={styles.inner}>
        <AttaimaLogo className={styles.attaimaLogo} aria-label="attaima" />
        <div className={styles.clientWrap}>
          {showCompanyLogo ? (
            <img
              className={styles.clientLogoImg}
              src={normalizedCompanyLogo}
              alt=""
              width={72}
              height={72}
              onError={() => setIsLogoBroken(true)}
            />
          ) : (
            <div className={styles.clientLogoPlaceholder} aria-hidden="true">
              {getCompanyInitials(companyName || userName)}
            </div>
          )}
          {companyName ? <div className={styles.companyName}>{companyName}</div> : null}
          {userName ? (
            <div className={styles.welcome}>
              {t('launchpad.splash.welcomeUser', {
                name: userName,
                defaultValue: 'Welcome, {{name}}',
              })}
            </div>
          ) : null}
          <div className={styles.sub}>{t('launchpad.splash.loading')}</div>
        </div>
        <div className={styles.spinner} aria-label={t('launchpad.splash.loading')} />
      </div>
    </div>
  );
}

export default LaunchpadSplash;
